import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { Box } from '@mui/material'
import LoadingPage from '../../pages/LoadingPage'
import { type ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import ROUTES from '../../consts/paths'
import { type CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import { divisionList, getLoading } from '../../store/slices/courseCatalogSlice/selectors'
import { getDivisionCoursesApi } from '../../store/slices/courseCatalogSlice/api'
import CreateAndEditCourseCatalog from './CreateNewCourseCatalog'
import CoursesCatalogTopbarActions from './CoursesCatalogTopbarActions'
import DeleteCourseCatalog from './DeleteCourseCatalog'
import PageHeader from '../../components/PageHeader'
import PAGE_TITLES from '../../consts/titles'
import { exportCsv } from '../../utils/exportCsv'
import { notification, closeNotification } from '../../components/Notifications'
import { DivisionManager } from '../../APIManager'
import { useMutation } from '@tanstack/react-query'
import { notifyError } from '../../utils/notifyError'
import ExportProcessingModal from '../../components/ExportProcessingModal'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

interface ExtendedParams extends ApplyFiltersAndDispatchParams {
  categoryName?: string
}

const CourseCatalog: React.FC = () => {
  const [params, setParams] = useState<ExtendedParams>({ categoryName: 'Course', scope: 'my' })
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_courseCatalog')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_courseCatalog')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  const loading = getLoading()
  const courseCatalogList = divisionList()
  const dispatch = useAppDispatch()

  const breadcrumbPrefixList: CustomBreadcrumbT[] = [{
    title: 'Course Catalog',
    path: ROUTES.COURSE_CATALOG
  }]

  const headerName = courseCatalogList?.header.headerName || 'Courses '

  useEffect(() => {
    void dispatch(getDivisionCoursesApi({ params }))
  }, [params])

  const { mutate: exportCourseCatalogCSV, isPending: exportCSVLoading } = useMutation({
    mutationFn: DivisionManager.exportCourseCatalogCSV,
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: (response, _variables, context) => {
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        closeNotification(context)
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: context })
      } else {
        closeNotification(context)
      }
    },
    onError: (error: any, _variables, context) => {
      closeNotification(context)
      notifyError(error)
    }
  })

  const handleExport = async (): Promise<void> => {
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    const exportParams = {
      search: params?.search || '',
      sort: params?.sort || undefined,
      categoryName: params?.categoryName || 'Course'
    }
    exportCourseCatalogCSV(exportParams)
  }

  const allowedActions = courseCatalogList?.actions ?? []

  const topbarActionsDropdownMenu = [
    {
      text: 'Export Courses (.csv)',
      action: () => { void handleExport() }
    }
  ]

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <Suspense fallback={<LoadingPage />}>
        <PageHeader title={PAGE_TITLES.COURSE_CATALOG} breadcrumb={breadcrumbPrefixList} />
        <AppTable
          list={courseCatalogList}
          deleteAction={<DeleteCourseCatalog isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          customTopbarAction={
            <CoursesCatalogTopbarActions
              params={params}
              allowedActions={allowedActions}
              setUploadTaskId={setUploadTaskId}
              isTaskRunning={isAnyTaskRunning}
              onTaskBlock={openBlockedModal}
            />
          }
          editAction={<CreateAndEditCourseCatalog params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          topbarActionsDropdownMenu={topbarActionsDropdownMenu}
          exportButtonProps={{ loading: exportCSVLoading || isAnyTaskRunning, disabled: exportCSVLoading || isAnyTaskRunning }}
          loading={loading}
          featureName="Course"
          params={params}
          setParams={setParams}
          showScopeToggle={true}
          scopeToggleTitle={headerName}
        />
      </Suspense>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showExportBlockedModal}
        onClose={() => { setShowExportBlockedModal(false) }}
        taskId={exportTaskId ?? ''}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showUploadBlockedModal}
        onClose={() => { setShowUploadBlockedModal(false) }}
        taskId={uploadTaskId ?? ''}
      />
    </Box>
  )
}

export default CourseCatalog
